import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';

// ============================================================================
// Types
// ============================================================================

export interface LoadingTraining {
  terminal: string;
  loadingPoint: string;
  validUntil: string;
}

export interface TransportationCompanyDriver {
  id: string;
  name: string;
  rfids: string[];
  loadingTrainings: LoadingTraining[];
}

export interface TransportationCompanyVehicle {
  id: string;
  register: string;
  compartments: number[];
  notes: string;
  adrCertificateValidUntil: string;
  isDeleted: boolean;
}

export interface TransportationCompany {
  id: string | null;
  name: string;
  address: string;
  email: string;
  notes: string;
  isDeleted: boolean;
  created: string | null;
  modified?: string | null;
  vehicles: TransportationCompanyVehicle[];
  drivers: TransportationCompanyDriver[];
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'transportationcompanies';

const fetchTransportationCompanies = async (): Promise<TransportationCompany[]> => {
  return apiRequest<void, TransportationCompany[]>(HttpMethod.GET, API_ENDPOINT);
};

const createTransportationCompany = async (company: TransportationCompany): Promise<TransportationCompany> => {
  return apiRequest<TransportationCompany, TransportationCompany>(HttpMethod.POST, API_ENDPOINT, company);
};

const updateTransportationCompany = async (company: TransportationCompany): Promise<TransportationCompany> => {
  return apiRequest<TransportationCompany, TransportationCompany>(
    HttpMethod.PUT,
    `${API_ENDPOINT}/${company.id}`,
    company
  );
};

const deleteTransportationCompany = async (company: TransportationCompany): Promise<TransportationCompany> => {
  return apiRequest<string, TransportationCompany>(HttpMethod.DELETE, `${API_ENDPOINT}/${company.id}`, company.id);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all transportation companies
 */
export const useTransportationCompaniesQuery = () => {
  return useQuery({
    queryKey: queryKeys.transportationCompanies.all,
    queryFn: fetchTransportationCompanies,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new transportation company
 */
export const useCreateTransportationCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTransportationCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transportationCompanies.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing transportation company
 */
export const useUpdateTransportationCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTransportationCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transportationCompanies.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete (soft delete) a transportation company
 */
export const useDeleteTransportationCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTransportationCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.transportationCompanies.all });
      toastService.success({ title: 'transportationCompanies.transportation_company_removed' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
